import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { NavigationEnd } from "@angular/router";
import { interval, Subscription } from 'rxjs';
import { Events } from '../../helpers/events';

// ionic
import { PopoverController } from '@ionic/angular';
import { ModalController } from '@ionic/angular';

// other libraries
import * as moment from "moment";

// rxjs
import { Subject } from "rxjs";
import { debounceTime, take, takeUntil } from "rxjs/operators";

// components
import { NewQuestionComponent } from "../../components/new-question/new-question.component";
import { AttendeeDetailComponent } from "../../components/attendee-detail/attendee-detail.component";

// services
import { AgendaService } from "../../services/agenda.service";

// models
import { Event } from "../../models/event";
import { User } from "../../models/user";
import { Agenda } from "../../models/agenda";
import { AgendaSpeaker } from "../../models/agenda-speaker";
import { SpeakerQuestion } from "../../models/speaker-question";

// config
import { environment } from "../../../environments/environment";

// extends
import { BasePage } from "../base";

@Component({
    selector: 'app-ask-the-speaker',
    templateUrl: './ask-the-speaker.page.html',
    styleUrls: ['./ask-the-speaker.page.scss'],
})

export class AskTheSpeakerPage extends BasePage implements OnInit, OnDestroy {

    // access constants for template
    public STATUS_CREATED = SpeakerQuestion.STATUS_CREATED;
    public STATUS_APPROVED = SpeakerQuestion.STATUS_APPROVED;
    public STATUS_DONE = SpeakerQuestion.STATUS_DONE;
    public STATUS_REFUSED = SpeakerQuestion.STATUS_REFUSED;
    public STATUS_DELETED = SpeakerQuestion.STATUS_DELETED;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for questions auto refresh
     *
     * @type {Subscription}
     */
    private subscriptionRefresh: Subscription;

    /**
     * chat box
     *
     * @type ViewChild
     */
    @ViewChild('questionBox') private scrollContainer: ElementRef;

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * active event for user
     *
     * @type Event
     */
    public event: Event = new Event;

    /**
     * current voting
     *
     * @type {Voting}
     */
    public agenda: Agenda[];

    /**
     * current voting
     *
     * @type {Voting}
     */
    public questionList: SpeakerQuestion[] = [];

    /**
     * info about last update on users profile
     *
     * @type {Date}
     */
    public lastUpdated: Date;

    /**
     * if we should show adding part
     *
     * @type {Date}
     */
    public add: boolean = false;

    /**
     * search string
     *
     * @type string
     */
    public search: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;


    /**
     * selected segment
     *
     * @type {String}
     */
    public segment: string = 'agenda';

    public descriptionLength: number = 120;

    public userScrolled: number = 0;

    /**
     * constructor
     *
     * @param translate
     * @param eventService
     * @param userService
     * @param appointmentService
     */
    constructor(
        public agendaService: AgendaService,
        public popoverController: PopoverController,
        public modalController: ModalController,
        public appEvents: Events
    ) {
        super();
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe)
        ).subscribe((user) => {
            if (user.id) {
                if (user.selected_participant_id != this.user.selected_participant_id) {
                    // participant have been switched, so delete question list
                    this.questionList = [];
                }
                this.user = user;
                this.event = this.user.selected_participant.event;
            }
        });
    }

    ionViewWillEnter() {
        this.userService.getCurrentUser()
            .pipe(
                take(1),
                debounceTime(0)
            ).subscribe(
                (user) => {
                    if (user.id) {
                        this.user = user;
                        this.event = this.user.selected_participant.event;

                        if (!this.event.id) {
                            // TODO redirect to no event if event is not available
                            this.router.navigate([this.plt.defaultLink + '/no-event']);
                        } else {
                            this.segment = 'agenda';
                            this.refreshAgenda();
                            this.refreshQuestions(true);

                            // cancel previous subscription
                            if (this.subscriptionRefresh) {
                                this.subscriptionRefresh.unsubscribe();
                            }

                            //load questions periodically
                            this.subscriptionRefresh = interval(30000).pipe(
                                takeUntil(this.ngUnsubscribe)
                            ).subscribe(() => {
                                this.refreshQuestions();
                            });

                            this.plt.activeSubscriptions.push(this.subscriptionRefresh);
                        }
                    }
                });
    }

    ionViewWillLeave() {
        // cancel previous subscription
        if (this.subscriptionRefresh) {
            this.subscriptionRefresh.unsubscribe();
        }
    }

    /**
     * refresh agenda
     *
     * @return void
     */
    public refreshAgenda($event?) {
        if (this.user.selected_participant) {
            let subscription = this.agendaService.getListByEvent(this.user.selected_participant.event_id).subscribe(
                (response) => {
                    this.agenda = [];
                    response.agendas.forEach((presentation) => {
                        if (presentation.visible) {
                            this.agenda.push(new Agenda(presentation));
                        }
                    })

                    // hide loading
                    if ($event) {
                        $event.target.complete();
                    } else {
                        this.loading = false;
                    }

                });
            this.plt.activeSubscriptions.push(subscription);
        }
    }

    /**
     * refresh quesions
     *
     * @return void
     */
    public refreshQuestions(scrollQuestions?) {
        let subscription = this.agendaService.getQuestionListByEvent(this.user.selected_participant.event_id).subscribe((questions) => {
            //this.questionList = [];
            questions.forEach((question) => {
                // if question was archived, we will show original status
                if (question.status == this.STATUS_DELETED) {
                    question.status = question.old_status;
                }
                let existing;
                if (existing = this.questionList.find(q => q.id == question.id)) {
                    existing.status = question.status;
                    existing.refuse_reason = question.refuse_reason;
                } else {
                    this.questionList.push(new SpeakerQuestion(question));
                }
            });


            if (scrollQuestions) {
                this.scrollQuestionsToBottom();
            }
            //
            //  // hide loading
            // if ($event) {
            //     $event.target.complete();
            // } else {
            //     this.loading = false;
            // }
        });
        this.plt.activeSubscriptions.push(subscription);

    }

    /**
     * open and process modal to send question
     */
    async presentSendQuestion(event, presentation, speaker) {
        var self = this;

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: NewQuestionComponent,
            //event: event,
            componentProps: {
                presentation: presentation,
                speaker: speaker,
                showSpeakerDetail: (speaker: AgendaSpeaker) => {
                    self.showSpeakerDetail(speaker);
                }
            },
            cssClass: 'form'
        });

        modal.onWillDismiss().then((data) => {

            if (data.data?.question) {
                this.userScrolled = 0;
                this.questionList.push(new SpeakerQuestion(data.data.question));
                this.scrollQuestionsToBottom();
                this.overlayService.showSuccess(data.data.success);
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    /**
     * scroll chat to bottom
     *
     * @return void
     */
    private scrollQuestionsToBottom(): void {
        try {
            setTimeout(() => {
                if (this.scrollContainer) {
                    this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
                }
            }, 150);
        } catch (err) {
            return;
        }
    }

    public searchPresentation() {
        this.agenda.forEach((agenda) => {
            if (this.search) {
                let search = '';
                agenda.translations.forEach((translation) => {
                    search += translation.title + ' ';
                });
                agenda.speakers.forEach((speaker) => {
                    search += speaker.name || speaker.participant.getFullName() + ' ';
                });
                if (search && search.search(new RegExp(this.search, "gi")) != -1) {
                    agenda.visible = true;
                } else {
                    agenda.visible = false;
                }
            } else {
                agenda.visible = true;
            }
        });
    }

    /**
     * go to speaker detail page
     *
     * @param speaker
     *
     * @return void
     */
    public async showSpeakerDetail(speaker: AgendaSpeaker) {
        if (speaker.participant_id) {
            if (!this.user.canProfileDetails(speaker.participant)) {
                return false;
            }

            this.routerExtService.softNavigate();

            const modal = await this.modalController.create({
                component: AttendeeDetailComponent,
                cssClass: 'attendee-modal',
                componentProps: {
                    attendee: speaker.participant
                }
            });

            modal.onWillDismiss().then((data) => {
                if (data.data?.action == 'close' || data.role == 'backdrop') {
                    this.routerExtService.softBack();
                }
            });

            return await modal.present();
        } else if (speaker.external_link) {
            window.open(speaker.external_link, "_system");
            return false;
        } else {
            return false;
        }
    }

    /**
     * set if user has scrolled
     *
     * @param $event
     *
     * @return void
     */
    public onScroll($event) {
        setTimeout(() => {
            if (this.scrollContainer.nativeElement.scrollTop == this.scrollContainer.nativeElement.scrollHeight) {
                this.userScrolled = 0;
            } else {
                this.userScrolled = this.scrollContainer.nativeElement.scrollTop;
            }
        }, 50);
    }

    /**
     * Method to change visible segment
     *
     * @param event
     */
    public tabChanged(segment) {
        this.segment = segment;
        this.scrollQuestionsToBottom();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.overlayService.closeModals();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

}
